import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const LNG_MIN = 7.95, LNG_MAX = 8.5, LAT_MIN = 44.96, LAT_MAX = 45.13;
const project = (lng, lat) => ({
  x: 80 + ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * 840,
  y: 60 + ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * 580,
});

const HILLS = [
  "M-20,180 C180,120 320,220 520,160 C720,100 860,200 1020,150",
  "M-20,300 C160,250 380,340 560,280 C740,220 880,320 1020,270",
  "M-20,420 C200,370 340,460 540,400 C740,340 900,430 1020,390",
  "M-20,540 C180,490 400,580 600,520 C800,460 920,550 1020,510",
  "M-20,650 C220,610 420,680 640,630 C860,580 940,650 1020,620",
  "M-20,80 C240,40 420,120 640,70 C860,20 940,90 1020,60",
];

const MapMonferrato = ({ villages }) => {
  const { t } = useLang();
  const [active, setActive] = useState(null);
  const selected = villages.find((v) => v.slug === active) || villages[0];
  const selectedText = selected ? t(`villagesText.${selected.slug}`) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch" data-testid="interactive-monferrato-map">
      <div className="lg:col-span-8 relative bg-[#EFECE6] border border-[#1C1B18]/8 overflow-hidden">
        <svg viewBox="0 0 1000 700" className="w-full h-full block" role="img" aria-label={t("home.mappa.title")}>
          <rect width="1000" height="700" fill="#EFECE6" />
          {HILLS.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={i === 2 ? "#C5A059" : "#666157"} strokeOpacity={i === 2 ? 0.35 : 0.14} strokeWidth={i === 2 ? 1.2 : 1} />
          ))}
          <text x="500" y="368" textAnchor="middle" fill="#1C1B18" fillOpacity="0.05" fontSize="64" letterSpacing="22" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            MONFERRATO
          </text>
          {villages.map((v) => {
            const { x, y } = project(v.longitude, v.latitude);
            const isActive = selected?.slug === v.slug;
            return (
              <g
                key={v.slug}
                data-testid={`map-marker-${v.slug}`}
                onClick={() => setActive(v.slug)}
                className="cursor-pointer"
                role="button"
                aria-label={v.name}
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setActive(v.slug)}
              >
                <motion.circle
                  cx={x} cy={y} r="10" fill="none" stroke="#C5A059" strokeWidth="1" pointerEvents="none"
                  style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  animate={{ scale: [1, 2.4], opacity: [0.6, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                />
                <circle cx={x} cy={y} r={isActive ? 8 : 5.5} fill={isActive ? "#C5A059" : "#1C1B18"} stroke="#F7F5F0" strokeWidth="2" pointerEvents="none" style={{ transition: "all .4s" }} />
                <circle cx={x} cy={y} r="24" fill="transparent" />
                <text x={x} y={y - 16} textAnchor="middle" fontSize="13" letterSpacing="2" pointerEvents="none"
                  fill={isActive ? "#9E7B36" : "#1C1B18"} fillOpacity={isActive ? 1 : 0.55}
                  style={{ fontFamily: "Plus Jakarta Sans, sans-serif", textTransform: "uppercase", fontWeight: 500 }}>
                  {v.name.toUpperCase()}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="absolute bottom-4 left-5 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#666157]">
          <MapPin size={12} className="text-[#C5A059]" /> {t("home.mappa.note")}
        </div>
      </div>

      <div className="lg:col-span-4">
        <AnimatePresence mode="wait">
          {selected && (
            <motion.div
              key={selected.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="h-full flex flex-col bg-[#121212] text-[#F7F5F0]"
              data-testid="map-village-card"
            >
              <div className="relative overflow-hidden" style={{ aspectRatio: "16/10" }}>
                <img src={selected.heroImage} alt={`${selected.name} — ${t("village.galleryAlt")}`} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212] to-transparent opacity-60" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] font-medium">{t("home.mappa.selected")}</p>
                <h3 className="font-serif-display text-3xl mt-2">{selected.name}</h3>
                <p className="font-serif-display italic text-[#F7F5F0]/60 mt-2">{selectedText?.tagline || selected.tagline}</p>
                <div className="mt-auto pt-6">
                  <Link
                    to={`/borghi/${selected.slug}`}
                    data-testid="map-explore-button"
                    className="inline-flex items-center gap-3 border border-[#C5A059]/60 text-[#F7F5F0] px-6 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A059] hover:text-[#121212] transition-colors duration-500"
                  >
                    {t("common.esplora")} <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MapMonferrato;
